import React, { useState, useEffect } from 'react';
import { Step1DateSelection } from './step1-dateSelection';
import { Step2GroupedActivities } from './step2-groupedActivities';
import { Step3FileUpload } from './step3-fileUpload';
import { Step4FileMatching } from './step4-fileMatching';
import { Step5UploadProgress } from './step5-uploadProgress';
import { groupActivitiesByProjectAndGroup, matchFilesToActivities } from './utils';
import { Activity, GroupedActivity, FileMatching, WizardState } from './interfaces';
import './styles.scss';

export const DynCardsUploadWizard: React.FC = () => {
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 1,
    startDate: null,
    endDate: null,
    activitiesData: [],
    selectedProject: null,
    selectedGroup: null,
    selectedGroupData: null,
    cardFiles: [],
    attachmentFiles: [],
    fileMatching: [],
    groupedActivities: [],
  });

  const [loading, setLoading] = useState(false);

  // Agrupar actividades cuando cambian los datos
  useEffect(() => {
    if (wizardState.activitiesData.length > 0) {
      const grouped = groupActivitiesByProjectAndGroup(wizardState.activitiesData);
      setWizardState(prev => ({ ...prev, groupedActivities: grouped }));
    }
  }, [wizardState.activitiesData]);

  // Hacer matching cuando cambian los archivos o actividades
  useEffect(() => {
    if (
      wizardState.cardFiles.length > 0 &&
      wizardState.selectedGroupData &&
      wizardState.selectedProject
    ) {
      const matching = matchFilesToActivities(
        wizardState.cardFiles,
        wizardState.selectedGroupData.activities,
        wizardState.selectedProject
      );
      setWizardState(prev => ({ ...prev, fileMatching: matching }));
    } else if (wizardState.selectedGroupData) {
      // Inicializar matching sin archivos
      const matching: FileMatching[] = wizardState.selectedGroupData.activities.map(activity => ({
        activityId: activity.id,
        activity,
        matchedFiles: [],
        status: activity.shouldHasCardsLoaded ? 'missing' : 'pending',
      }));
      setWizardState(prev => ({ ...prev, fileMatching: matching }));
    }
  }, [wizardState.cardFiles, wizardState.selectedGroupData, wizardState.selectedProject]);

  const handleStep1Next = (activities: Activity[], startDate: string, endDate: string) => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 2,
      activitiesData: activities,
      startDate,
      endDate,
    }));
  };

  const handleStep2Next = (projectName: string, groupData: GroupedActivity['groups'][0]) => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 3,
      selectedProject: projectName,
      selectedGroup: groupData.groupAssigned,
      selectedGroupData: groupData,
      cardFiles: [],
      attachmentFiles: [],
    }));
  };

  const handleStep2Back = () => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 1,
      selectedProject: null,
      selectedGroup: null,
      selectedGroupData: null,
    }));
  };

  const handleStep3Next = () => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 4,
    }));
  };

  const handleStep3Back = () => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 2,
      cardFiles: [],
      attachmentFiles: [],
    }));
  };

  const handleStep4Next = () => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 5,
    }));
  };

  const handleStep4Back = () => {
    setWizardState(prev => ({
      ...prev,
      currentStep: 3,
    }));
  };

  const handleCardFilesChange = (files: File[]) => {
    setWizardState(prev => ({ ...prev, cardFiles: files }));
  };

  const handleAttachmentFilesChange = (files: File[]) => {
    setWizardState(prev => ({ ...prev, attachmentFiles: files }));
  };

  const handleFileMatchingChange = (matching: FileMatching[]) => {
    setWizardState(prev => ({ ...prev, fileMatching: matching }));
  };

  // Función para determinar si un paso está disponible para navegar
  const isStepAvailable = (step: number): boolean => {
    // Siempre se puede navegar al paso actual
    if (step === wizardState.currentStep) return true;
    
    // Se puede navegar a pasos anteriores
    if (step < wizardState.currentStep) return true;
    
    // Para pasos futuros, verificar que los pasos anteriores estén completos
    switch (step) {
      case 2:
        return wizardState.activitiesData.length > 0;
      case 3:
        return wizardState.selectedGroupData !== null && wizardState.selectedProject !== null;
      case 4:
        return wizardState.cardFiles.length > 0 || wizardState.selectedGroupData !== null;
      case 5:
        return wizardState.fileMatching.length > 0;
      default:
        return false;
    }
  };

  const handleStepClick = (step: number) => {
    if (isStepAvailable(step)) {
      setWizardState(prev => ({ ...prev, currentStep: step }));
    }
  };

  const renderStep = () => {
    switch (wizardState.currentStep) {
      case 1:
        return (
          <Step1DateSelection
            onNext={handleStep1Next}
            loading={loading}
            setLoading={setLoading}
          />
        );
      case 2:
        return (
          <Step2GroupedActivities
            groupedActivities={wizardState.groupedActivities}
            onNext={handleStep2Next}
            onBack={handleStep2Back}
          />
        );
      case 3:
        if (!wizardState.selectedGroupData || !wizardState.startDate || !wizardState.endDate || !wizardState.selectedProject) {
          return null;
        }
        return (
          <Step3FileUpload
            selectedGroupData={wizardState.selectedGroupData}
            selectedProject={wizardState.selectedProject}
            startDate={wizardState.startDate}
            endDate={wizardState.endDate}
            cardFiles={wizardState.cardFiles}
            attachmentFiles={wizardState.attachmentFiles}
            onCardFilesChange={handleCardFilesChange}
            onAttachmentFilesChange={handleAttachmentFilesChange}
            onNext={handleStep3Next}
            onBack={handleStep3Back}
          />
        );
      case 4:
        if (!wizardState.selectedGroupData || !wizardState.selectedProject) {
          return null;
        }
        return (
          <Step4FileMatching
            fileMatching={wizardState.fileMatching}
            cardFiles={wizardState.cardFiles}
            activities={wizardState.selectedGroupData.activities}
            projectName={wizardState.selectedProject}
            onFileMatchingChange={handleFileMatchingChange}
            onCardFilesChange={handleCardFilesChange}
            onNext={handleStep4Next}
            onBack={handleStep4Back}
          />
        );
      case 5:
        if (!wizardState.selectedGroupData || !wizardState.selectedProject) {
          return null;
        }
        return (
          <Step5UploadProgress
            fileMatching={wizardState.fileMatching}
            attachmentFiles={wizardState.attachmentFiles}
            selectedGroupData={wizardState.selectedGroupData}
            projectName={wizardState.selectedProject}
            startDate={wizardState.startDate || ''}
            endDate={wizardState.endDate || ''}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="dyn-cards-wizard-root">
      {/* Indicador de pasos */}
      <div className="dyn-cards-wizard-steps-indicator">
        {[1, 2, 3, 4, 5].map((step) => {
          const isAvailable = isStepAvailable(step);
          return (
            <div
              key={step}
              className={`dyn-cards-wizard-step-indicator ${
                step === wizardState.currentStep
                  ? 'active'
                  : step < wizardState.currentStep
                  ? 'completed'
                  : ''
              } ${isAvailable ? 'clickable' : 'disabled'}`}
              onClick={() => handleStepClick(step)}
            >
              <div className="dyn-cards-wizard-step-number">{step}</div>
              <div className="dyn-cards-wizard-step-label">
                {step === 1 && 'Fechas'}
                {step === 2 && 'Cuadrilla'}
                {step === 3 && 'Archivos'}
                {step === 4 && 'Matching'}
                {step === 5 && 'Subida'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contenido del paso actual */}
      <div className="dyn-cards-wizard-content">{renderStep()}</div>
    </div>
  );
};

